/**
 * Optional add-on products shown alongside the main product.
 *
 * Each add-on has a quantity stepper (0 = not included, N = N x price) instead
 * of a checkbox, because several add-ons are ordered in multiples. This element
 * owns the stepper behaviour and exposes `selectedItems` to the product form,
 * which appends them to a single /cart/add.js payload so the cart opens once.
 * It emits a bubbling `addons:change` event so <product-total> can re-total.
 *
 * See docs/INSTALL.md for the product-form patch.
 */
class ProductAddons extends HTMLElement {
  connectedCallback() {
    this.formatter = this.buildFormatter();
    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onInput);
    this.addEventListener('change', this.onChange);
    this.querySelectorAll('.addons__qty-input').forEach((input) => this.syncCard(input));
    this.setupCollapse();
  }

  // Currency formatter matching <product-total> (narrow symbol, whole dollars).
  buildFormatter() {
    const currency = this.dataset.currency || 'USD';
    const locale = this.dataset.locale || undefined;
    const options = {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    };
    try {
      return new Intl.NumberFormat(locale, options);
    } catch (error) {
      delete options.currencyDisplay;
      return new Intl.NumberFormat(locale, options);
    }
  }

  // Collapse a long list behind a "Show all N" toggle. Progressive enhancement:
  // with no JS every add-on is visible; JS collapses to the first few. Starts
  // expanded if a hidden add-on is already selected so the total stays legible.
  setupCollapse() {
    this.toggle = this.querySelector('.addons__toggle');
    if (!this.toggle || !this.querySelector('.addons__item--extra')) return;

    const hasSelectedExtra = Array.from(
      this.querySelectorAll('.addons__item--extra .addons__qty-input')
    ).some((input) => (parseInt(input.value, 10) || 0) > 0);

    this.collapsed = !hasSelectedExtra;
    this.toggle.hidden = false;
    this.applyCollapse();

    this.toggle.addEventListener('click', () => {
      this.collapsed = !this.collapsed;
      this.applyCollapse();
    });
  }

  applyCollapse() {
    this.classList.toggle('addons--collapsed', this.collapsed);
    this.classList.toggle('addons--expanded', !this.collapsed);
    this.toggle.setAttribute('aria-expanded', String(!this.collapsed));
    const textEl = this.toggle.querySelector('.addons__toggle-text');
    if (textEl) {
      textEl.textContent = this.collapsed ? this.toggle.dataset.labelMore : this.toggle.dataset.labelLess;
    }
  }

  onClick = (event) => {
    // Add-on details modal: open, close, backdrop click, thumbnail swap.
    const openBtn = event.target.closest('.addons__open');
    if (openBtn) {
      const dialog = document.getElementById(openBtn.getAttribute('aria-controls'));
      if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
      return;
    }
    if (event.target.closest('.addon-modal__close')) {
      event.target.closest('dialog')?.close();
      return;
    }
    if (event.target.tagName === 'DIALOG' && event.target.classList.contains('addon-modal')) {
      event.target.close();
      return;
    }
    const thumb = event.target.closest('.addon-modal__thumb');
    if (thumb && thumb.dataset.full) {
      const main = thumb.closest('.addon-modal__media')?.querySelector('.addon-modal__main-img');
      if (main) {
        main.removeAttribute('srcset');
        main.removeAttribute('sizes');
        main.src = thumb.dataset.full;
      }
      return;
    }

    // Quantity stepper
    const button = event.target.closest('[data-qty-step]');
    if (!button) return;
    const input = button.parentElement.querySelector('.addons__qty-input');
    if (!input || input.disabled) return;

    const step = parseInt(button.dataset.qtyStep, 10) || 0;
    const min = parseInt(input.min, 10) || 0;
    let value = (parseInt(input.value, 10) || 0) + step;
    if (value < min) value = min;
    input.value = value;
    this.commit(input);
  };

  onInput = (event) => {
    const input = event.target.closest('.addons__qty-input');
    if (!input) return;
    let value = parseInt(input.value, 10);
    if (Number.isNaN(value) || value < 0) value = 0;
    input.value = value;
    this.commit(input);
  };

  // Finish selector on a multi-variant add-on (e.g. floor treatment). Point the
  // card's quantity input at the chosen variant and swap the displayed price, so
  // the cart payload, running total and summary all follow the selection.
  onChange = (event) => {
    const select = event.target.closest('.addons__finish');
    if (!select) return;
    const opt = select.selectedOptions[0];
    const card = select.closest('.addons__card');
    const input = card && card.querySelector('.addons__qty-input');
    if (!opt || !input) return;
    input.dataset.addonId = opt.value;
    input.dataset.addonPrice = opt.dataset.price;
    input.dataset.addonTitle = opt.dataset.title;
    const priceEl = card.querySelector('.addons__price');
    if (priceEl && opt.dataset.money) priceEl.textContent = opt.dataset.money;
    this.commit(input);
  };

  commit(input) {
    this.syncCard(input);
    this.dispatchEvent(new CustomEvent('addons:change', { bubbles: true }));
  }

  syncCard(input) {
    const card = input.closest('.addons__card');
    if (!card) return;
    const qty = parseInt(input.value, 10) || 0;
    card.classList.toggle('is-selected', qty > 0);
    // Reflect the price for the chosen quantity so 2x costs twice 1x. Quantity 0
    // shows the per-item price (x1) as the default.
    const priceEl = card.querySelector('.addons__price');
    if (priceEl && this.formatter) {
      const unit = parseInt(input.dataset.addonPrice, 10) || 0;
      priceEl.textContent = this.formatter.format((unit * Math.max(qty, 1)) / 100);
    }
  }

  get selectedItems() {
    return Array.from(this.querySelectorAll('.addons__qty-input'))
      .map((input) => ({
        id: Number(input.dataset.addonId),
        quantity: parseInt(input.value, 10) || 0,
      }))
      .filter((item) => item.id && item.quantity > 0);
  }

  clear() {
    this.querySelectorAll('.addons__qty-input').forEach((input) => {
      input.value = 0;
      this.syncCard(input);
    });
    this.dispatchEvent(new CustomEvent('addons:change', { bubbles: true }));
  }
}

if (!customElements.get('product-addons')) {
  customElements.define('product-addons', ProductAddons);
}

/**
 * Running order total shown above the buy CTA: the selected variant price plus
 * every add-on quantity x price. Re-totals on `addons:change` and on Dawn's
 * variantChange pub/sub (price can differ per variant).
 */
class ProductTotal extends HTMLElement {
  connectedCallback() {
    this.base = parseInt(this.dataset.basePrice, 10) || 0;
    this.amountEl = this.querySelector('[data-total-amount]');
    this.formatter = this.buildFormatter();
    this.variant = null;

    this.recompute = () => this.render();
    document.addEventListener('addons:change', this.recompute);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event && event.data && event.data.variant;
        // Hold the last real selection when a combination resolves to nothing. The enquiry is
        // about what the buyer picked, and falling back to the first variant would name the
        // wrong thing on the invoice, which is worse than naming a sold-out one.
        if (variant) this.variant = variant;
        if (variant && typeof variant.price === 'number') this.base = variant.price;
        this.render();
      });
    }

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('addons:change', this.recompute);
    if (this.unsubscribe) this.unsubscribe();
  }

  buildFormatter() {
    const currency = this.dataset.currency || 'USD';
    const locale = this.dataset.locale || undefined;
    const options = {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    };
    try {
      return new Intl.NumberFormat(locale, options);
    } catch (error) {
      delete options.currencyDisplay;
      return new Intl.NumberFormat(locale, options);
    }
  }

  get addonsTotal() {
    let sum = 0;
    document.querySelectorAll('product-addons .addons__qty-input').forEach((input) => {
      sum += (parseInt(input.value, 10) || 0) * (parseInt(input.dataset.addonPrice, 10) || 0);
    });
    return sum;
  }

  render() {
    if (!this.amountEl) return;
    const total = this.base + this.addonsTotal;
    this.amountEl.textContent = this.formatter.format(total / 100);
    this.updateDerived(total);
  }

  // Keep the reserve deposit + pay-in-full amounts (and the enquiry hidden fields)
  // in sync with the live total, so they reflect the selected variant + add-ons
  // rather than the base price. Percentages come from data-* on this element.
  updateDerived(total) {
    const fmt = (cents) => this.formatter.format(cents / 100);
    const depPct = parseFloat(this.dataset.depositPct) || 0;
    const prepayPct = parseFloat(this.dataset.prepayPct) || 0;
    const deposit = Math.round((total * depPct) / 100);
    const ppSaving = Math.round((total * prepayPct) / 100);
    const ppTotal = total - ppSaving;

    const values = {
      total: fmt(total),
      deposit: fmt(deposit),
      ppTotal: fmt(ppTotal),
      ppSaving: fmt(ppSaving),
      depPct: String(depPct),
      prepayPct: String(prepayPct),
      addons: this.addonsSummary(),
    };
    Object.assign(values, this.variantValues());

    document.querySelectorAll('[data-rd-amount]').forEach((el) => {
      el.textContent = values.deposit;
    });
    document.querySelectorAll('[data-pp-total]').forEach((el) => {
      el.textContent = values.ppTotal;
    });
    document.querySelectorAll('[data-pp-saving]').forEach((el) => {
      el.textContent = values.ppSaving;
    });

    // Enquiry hidden fields: value is a small template with {key} placeholders.
    document.querySelectorAll('[data-total-tpl]').forEach((input) => {
      input.value = input.dataset.totalTpl.replace(/\{(\w+)\}/g, (match, key) =>
        key in values ? values[key] : match
      );
    });
  }

  // Which item the enquiry is actually about. A title and a dollar figure do not identify a size,
  // a wall thickness or a glazing option, and the invoice is raised by a person reading this email.
  // Falls back to the data-* attributes Liquid filled with the variant the page loaded on, so the
  // fields are still right when this script has not run.
  variantValues() {
    const v = this.variant;
    const id = (v && v.id) || this.dataset.variantId || '';
    const url = this.dataset.productUrl || '';
    return {
      variant: (v && v.title) || this.dataset.variantTitle || '',
      sku: (v && v.sku) || this.dataset.sku || '',
      variantId: String(id),
      productUrl: id ? `${url}?variant=${id}` : url,
    };
  }

  addonsSummary() {
    const parts = [];
    document.querySelectorAll('product-addons .addons__qty-input').forEach((input) => {
      const qty = parseInt(input.value, 10) || 0;
      if (qty > 0) parts.push(`${input.dataset.addonTitle || 'Add-on'} x ${qty}`);
    });
    return parts.length ? parts.join('; ') : 'None';
  }
}

if (!customElements.get('product-total')) {
  customElements.define('product-total', ProductTotal);
}
